import React from 'react';
import { PlayIcon, ClockIcon, HeartIcon, MoreHorizontalIcon, ArrowLeftIcon } from 'lucide-react';
interface Track {
  id: string;
  title: string;
  artist: string;
  album?: string;
  duration: string;
  popularity?: number;
}
interface PlaylistViewProps {
  playlist: any;
  isAlbum?: boolean;
  onBack: () => void;
}
export const PlaylistView = ({
  playlist,
  isAlbum = false,
  onBack
}: PlaylistViewProps) => {
  // Ensure playlist exists before accessing properties
  if (!playlist) {
    return <div className="min-h-full bg-gradient-to-b from-[#333333] to-[#121212] p-8" data-id="element-504">
        <button onClick={onBack} className="p-2 rounded-full bg-black/40 hover:bg-black/60 transition-colors" data-id="element-505">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-506" />
        </button>
        <div className="text-center mt-8" data-id="element-507">Loading...</div>
      </div>;
  }
  // Generate mock tracks if not provided, ensuring we always have an array
  const tracks: Track[] = Array.isArray(playlist.tracks) ? playlist.tracks : Array(12).fill(null).map((_, i) => ({
    id: `track-${i}`,
    title: isAlbum ? ['Blinding Lights', 'Save Your Tears', 'In Your Eyes', 'After Hours', 'Heartless', 'Too Late'][i % 6] : ['Bohemian Rhapsody', 'Stairway to Heaven', 'Imagine', 'Billie Jean', 'Smells Like Teen Spirit', 'Let It Be'][i % 6],
    artist: isAlbum ? playlist.artist : ['Queen', 'Led Zeppelin', 'John Lennon', 'Michael Jackson', 'Nirvana', 'The Beatles'][i % 6],
    album: isAlbum ? playlist.title : ['A Night at the Opera', 'Led Zeppelin IV', 'Imagine', 'Thriller', 'Nevermind', 'Let It Be'][i % 6],
    duration: `${Math.floor(Math.random() * 2) + 2}:${Math.floor(Math.random() * 59).toString().padStart(2, '0')}`,
    popularity: Math.floor(Math.random() * 100)
  }));
  const totalDuration = '1 hr 12 min';
  return <div className="min-h-full bg-gradient-to-b from-[#333333] to-[#121212]" data-id="element-508">
      {/* Header */}
      <div className="p-6 flex flex-col md:flex-row items-start md:items-end space-y-6 md:space-y-0 md:space-x-6" data-id="element-509">
        <button onClick={onBack} className="absolute top-4 left-4 p-2 rounded-full bg-black/40 hover:bg-black/60 transition-colors" data-id="element-510">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-511" />
        </button>
        <div className="w-48 h-48 flex-shrink-0 shadow-2xl" data-id="element-512">
          <img src={playlist.imageUrl || 'https://images.unsplash.com/photo-1611339555312-e607c8352fd7?w=300&h=300&fit=crop'} alt={playlist.title} className="w-full h-full object-cover" onError={e => {
          e.currentTarget.src = 'https://images.unsplash.com/photo-1611339555312-e607c8352fd7?w=300&h=300&fit=crop';
        }} data-id="element-513" />
        </div>
        <div className="flex flex-col justify-end max-w-full" data-id="element-514">
          <p className="text-sm uppercase font-bold" data-id="element-515">
            {isAlbum ? 'Album' : 'Playlist'}
          </p>
          <h1 className="text-3xl md:text-5xl font-bold my-2 break-words" data-id="element-516">
            {playlist.title}
          </h1>
          <div className="flex flex-wrap items-center text-sm text-gray-300 space-x-1" data-id="element-517">
            {isAlbum ? <>
                <span className="font-bold text-white" data-id="element-518">{playlist.artist}</span>
                <span data-id="element-519">•</span>
                <span data-id="element-520">{playlist.releaseYear}</span>
                <span data-id="element-521">•</span>
                <span data-id="element-522">{tracks.length} songs</span>
                <span data-id="element-523">•</span>
                <span data-id="element-524">{totalDuration}</span>
              </> : <>
                <span className="font-bold text-white" data-id="element-525">Spotify</span>
                <span data-id="element-526">•</span>
                <span data-id="element-527">{tracks.length} songs</span>
                <span data-id="element-528">•</span>
                <span data-id="element-529">{totalDuration}</span>
              </>}
          </div>
        </div>
      </div>
      {/* Controls */}
      <div className="px-6 py-4 flex items-center space-x-4" data-id="element-530">
        <button className="bg-green-500 text-black rounded-full p-3 hover:scale-105 transition-transform" data-id="element-531">
          <PlayIcon className="w-6 h-6 md:w-8 md:h-8" data-id="element-532" />
        </button>
        <button className="text-white hover:text-green-500 transition-colors" data-id="element-533">
          <HeartIcon className="w-6 h-6 md:w-8 md:h-8" data-id="element-534" />
        </button>
        <button className="text-white hover:text-green-500 transition-colors" data-id="element-535">
          <MoreHorizontalIcon className="w-6 h-6 md:w-8 md:h-8" data-id="element-536" />
        </button>
      </div>
      {/* Tracks list */}
      <div className="px-4 md:px-6 pb-28 overflow-x-hidden" data-id="element-537">
        <table className="w-full text-left text-gray-400" data-id="element-538">
          <thead data-id="element-539">
            <tr className="border-b border-white/10" data-id="element-540">
              <th className="py-2 w-8" data-id="element-541">#</th>
              <th className="py-2" data-id="element-542">TITLE</th>
              {!isAlbum && <th className="py-2 hidden md:table-cell" data-id="element-543">ALBUM</th>}
              <th className="py-2 hidden md:table-cell" data-id="element-544">DATE ADDED</th>
              <th className="py-2 text-right" data-id="element-545">
                <ClockIcon className="w-5 h-5 inline" data-id="element-546" />
              </th>
            </tr>
          </thead>
          <tbody data-id="element-547">
            {tracks.map((track, index) => <tr key={track.id} className="hover:bg-white/10 group" data-id="element-548">
                <td className="py-3 px-1" data-id="element-549">
                  <div className="flex items-center justify-center w-6" data-id="element-550">
                    <span className="group-hover:hidden" data-id="element-551">{index + 1}</span>
                    <PlayIcon className="w-4 h-4 hidden group-hover:block" data-id="element-552" />
                  </div>
                </td>
                <td className="py-3" data-id="element-553">
                  <div className="flex items-center" data-id="element-554">
                    <div className="overflow-hidden" data-id="element-555">
                      <div className="text-white truncate max-w-[200px] md:max-w-none" data-id="element-556">
                        {track.title}
                      </div>
                      <div className="text-sm truncate max-w-[200px] md:max-w-none" data-id="element-557">
                        {track.artist}
                      </div>
                    </div>
                  </div>
                </td>
                {!isAlbum && <td className="py-3 hidden md:table-cell" data-id="element-558">{track.album}</td>}
                <td className="py-3 hidden md:table-cell" data-id="element-559">3 days ago</td>
                <td className="py-3 text-right px-1" data-id="element-560">{track.duration}</td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
};